/**
 * ================================================================================================
 *  THE PAGE THE REGISTRATION LINK LANDS ON — AND WHERE THE ORGANISATION IS ACTUALLY CREATED
 * ================================================================================================
 *
 * §10.2. It does two things, in two steps, and the second one is the only act in the public journey
 * that creates anything:
 *
 *   1. ON ARRIVAL it presents the token. That proves the address and nothing more — still no
 *      organisation, no user, no subscription.
 *   2. THEN it asks for the plan, the kind of organisation and an explicit agreement to the figures,
 *      and only then creates the office.
 *
 * ── WHY IT IS NOT `/verify-email` ──────────────────────────────────────────────────────────────
 *
 * That address belongs to the LOGIN-ADDRESS CHANGE, which confirms a change on an account that
 * already exists. A registering stranger arriving there would meet a screen about an account they
 * do not have — so `signup_verification` is its own mail kind with its own landing path, and
 * `buildLink` would have refused the path until this file existed.
 *
 * ── THE TOKEN IS PRESENTED ONCE ────────────────────────────────────────────────────────────────
 *
 * `useRef`, for `EmailChangeConfirmScreen`'s reason: React mounts an effect twice under StrictMode,
 * and the token is SINGLE-USE. A second presentation consumes nothing and answers "this link is not
 * usable", which would show a person a failure for a verification that had just succeeded.
 *
 * ── STEP FOUR SITS BETWEEN THE CONSENT AND THE CREATE BUTTON. ו-16, 2026-10-30, 0034. ─────────
 *
 * The Owner: *"בדיקת אמצעי תשלום אצל הספק, ורק אז תחילת הניסיון ללא חיוב. אין לאפשר שימוש עסקי
 * מלא דרך מסלול שעוקף את דרישת אמצעי התשלום."*
 *
 * So the create button is DISABLED until the check has passed, and the screen says why rather than
 * leaving a dead control. The gate is not here — `complete` refuses, `SqlSignupStore.complete`'s
 * predicate refuses, and `CK_signup_request_completed_has_payment_method` makes the row
 * unrepresentable. This is the step being VISIBLE, which is a different job: a person who cannot
 * press a button and is not told why presses it anyway and reads a refusal as a fault.
 *
 * AND ON THIS ESTATE IT REFUSES, by design. Cardcom's `TerminalNumber` and `ApiName` are
 * outstanding Owner actions, so `unconfiguredPaymentMethods()` answers `provider_not_configured`
 * and the screen shows the server's own reason. There is no card field here and there must not be
 * one until there is a provider: a card is typed into the PROVIDER'S page, which hands this browser
 * a one-time handle. §9.2/§9.3 build that, and they are blocked on the same two values.
 *
 * ── AND IT SETS NO PASSWORD ────────────────────────────────────────────────────────────────────
 *
 * There is no password field on this screen and there must never be one. Creating the organisation
 * mails the first administrator an INVITATION, and a password is set through
 * `auth.invitation.accept` — the product's one credential writer. The success text says so, because
 * a person who expected to finish here needs to know a second mail is coming.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { StateBlock } from '../components/StateBlock';
import { classifyActionError, type ActionFailure } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { useLocale } from '../i18n/LocaleProvider';
import type { TextKey } from '../i18n/catalogue';
import type { PublicPlanOfferView } from '../api/contract';
import { leaveTo } from '../api/leave';
import { routes } from '../routes';

/** The two kinds the public door admits. `internal` is this estate's own and is refused. */
const KINDS: readonly { readonly id: string; readonly label: TextKey }[] = [
  { id: 'cpa_firm', label: 'signup.kind.cpa_firm' },
  { id: 'direct_business', label: 'signup.kind.direct_business' },
];

type Stage =
  | { readonly kind: 'verifying' }
  | { readonly kind: 'verified'; readonly signupUid: string; readonly legalName: string }
  | { readonly kind: 'created' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

/**
 * Where step four has got to. `none` is "not attempted", which is not a failure.
 *
 * `asking` AND `leaving` ARE TWO STATES AND NOT ONE. The first is the server talking to the
 * provider; the second is this browser on its way out. `window.location.assign` is not
 * instantaneous, and a button that stays enabled across it invites a second page -- which the
 * server would supersede, so nothing breaks, but the customer would be looking at a page this
 * product has already discarded.
 */
type PaymentStage =
  | { readonly kind: 'none' }
  | { readonly kind: 'asking' }
  | { readonly kind: 'leaving' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'checked'; readonly provider: string }
  | { readonly kind: 'refused'; readonly failure: ActionFailure }
  /**
   * THE PROVIDER HAS NO PAGE ON THIS ESTATE, AND THAT IS A DIFFERENT THING FROM A REFUSAL.
   *
   * It is what `unconfiguredPaymentMethods` and the sandbox adapter both answer, and the screen's
   * correct response is to fall back to the direct check rather than to show a failure. On
   * production that check then answers `provider_not_configured` -- the honest end of the journey
   * on an estate with no terminal. On a Staging estate it succeeds, which is what makes a test
   * registration completable without a real charge.
   */
  | { readonly kind: 'noProviderPage'; readonly failure: ActionFailure };

export function SignupVerifyScreen({ token }: { readonly token: string | null }): JSX.Element {
  const { t } = useLocale();
  const [stage, setStage] = useState<Stage>({ kind: 'verifying' });
  const [planCode, setPlanCode] = useState('');
  /*
   * ── THE ARTICLES CHOSEN BESIDE THE PLAN. **ITEM 1, 2026-11-03.** ──────────────────────────
   *
   * *"בחירת מודולים בהרשמה העצמית."* Held as a sorted list rather than a Set so that two
   * selections of the same articles produce the same value, which is what lets the quote below
   * be keyed on it without re-asking the server for a choice that has not changed.
   *
   * CLEARED WHEN THE PLAN CHANGES, because the offer is PER PLAN: an article addable to one plan
   * may be included in another, and a selection carried across would be a customer buying
   * something their new plan already gives them -- or being refused by the server for a code it
   * no longer offers, after they had chosen it.
   */
  const [chosenSkus, setChosenSkus] = useState<readonly string[]>([]);
  const [tenantKind, setTenantKind] = useState(KINDS[0]?.id ?? 'cpa_firm');
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [payment, setPayment] = useState<PaymentStage>({ kind: 'none' });
  /*
   * THE PROVIDER'S ONE-TIME HANDLE, AND THERE IS NO FIELD FOR IT ON PURPOSE.
   *
   * A card is typed into the PROVIDER'S own page, which hands this browser a single-use reference.
   * Until there is a provider there is nothing to issue one, so this stays empty and the server
   * answers `provider_not_configured` -- which it decides BEFORE it looks at this value, so an
   * empty handle never reads as the person's mistake. A text box here would invite somebody to
   * paste a card number into the one field on this screen that reaches the server.
   */
  const [providerHandle] = useState('');
  const presented = useRef(false);

  const read = useCallback(() => callOperation('public.signup.plans', {}), []);
  const offers = useServerData(read);

  /*
   * THE ARTICLES THE CHOSEN PLAN MAY BE EXTENDED WITH. **ITEM 1.** Derived from the offer the
   * server already sent rather than read again: the list is per plan, and asking for it a second
   * time could answer differently from the one the customer is looking at.
   */
  const addOnsForPlan =
    offers.result.state === 'loaded'
      ? (offers.result.data.plans.find((offer) => offer.planCode === planCode)?.addOns ?? [])
      : [];

  useEffect(() => {
    if (token === null || token === '' || presented.current) return;
    presented.current = true;
    void (async () => {
      try {
        const result = await callOperation('public.signup.verify', { body: { token } });
        setStage({
          kind: 'verified',
          signupUid: result.signupUid,
          legalName: result.legalName,
        });
      } catch (error) {
        setStage({ kind: 'failed', failure: classifyActionError(error) });
      }
    })();
  }, [token]);

  /*
   * THE CHECK, AND IT SENDS NO CARD. The body carries the request uid and the provider's own
   * one-time handle — which on this estate is empty, because there is no provider page to issue
   * one. The server asks "is a provider configured" BEFORE it looks at the handle, so what comes
   * back here is `provider_not_configured` and not a complaint about a missing reference.
   */
  /*
   * ── STEP FOUR, THE WAY A PROVIDER-HOSTED PAGE ACTUALLY WORKS ─────────────────────────────────
   *
   * *"\u05dc\u05e7\u05d5\u05d7 \u05de\u05d6\u05d9\u05df \u05d0\u05de\u05e6\u05e2\u05d9 \u05ea\u05e9\u05dc\u05d5\u05dd \u05d3\u05e8\u05da \u05d4\u05de\u05de\u05e9\u05e7 \u05d4\u05de\u05d0\u05d5\u05d1\u05d8\u05d7 \u05e9\u05dc \u05e7\u05d0\u05e8\u05d3\u05e7\u05d5\u05dd."* The server asks Cardcom for a page and
   * answers its address; this browser goes there. The card is typed into THEIR page, and the
   * result comes back to `PaymentReturnScreen`, which asks the server what the provider said.
   *
   * THE BODY CARRIES NO FIGURE. Cardcom requires an `Amount` even for a card validation, and the
   * server resolves it from the published price book -- the same path that put the price on this
   * screen. A price in this body would be a price the browser chose.
   *
   * A REFUSAL NAMED `provider_page_unavailable` IS NOT AN ERROR HERE. It means this estate has no
   * provider page, so the direct check below is the path; see `noProviderPage`.
   */
  function goToPaymentPage(signupUid: string): void {
    setPayment({ kind: 'asking' });
    void callOperation('public.payment.page', { body: { signupUid, planCode } })
      .then((opened) => {
        setPayment({ kind: 'leaving' });
        leaveTo(opened.redirectUrl);
      })
      .catch((error: unknown) => {
        const failure = classifyActionError(error);
        setPayment(
          failure.kind === 'refused' && failure.reason === 'provider_page_unavailable'
            ? { kind: 'noProviderPage', failure }
            : { kind: 'refused', failure },
        );
      });
  }

  function checkPaymentMethod(signupUid: string): void {
    setPayment({ kind: 'checking' });
    void callOperation('public.signup.payment_method', {
      body: { signupUid, providerHandle: providerHandle },
    })
      .then((result) => {
        setPayment({ kind: 'checked', provider: result.provider });
      })
      .catch((error: unknown) => {
        setPayment({ kind: 'refused', failure: classifyActionError(error) });
      });
  }

  function create(event: FormEvent, signupUid: string): void {
    event.preventDefault();
    setSending(true);
    void callOperation('public.signup.complete', {
      /*
       * NO PRICE AND NO PASSWORD IN THIS BODY. The figures were shown by `public.signup.plans` and
       * the server resolves them again before recording the consent — a price returned from here
       * would be a price the browser chose. `consentGiven` is the agreement itself and the server
       * refuses without it.
       */
      /*
       * THE CODES AND NOTHING ELSE. **ITEM 1.** The server resolves each one against the chosen
       * plan's own offer and prices it from the published book -- a price sent from here would be
       * a price the browser chose, which is the same rule the comment above states about the plan.
       */
      body: { signupUid, planCode, skuCodes: chosenSkus, tenantKind, consentGiven: true },
    })
      .then(() => {
        setStage({ kind: 'created' });
      })
      .catch((error: unknown) => {
        setStage({ kind: 'failed', failure: classifyActionError(error) });
      })
      .finally(() => {
        setSending(false);
      });
  }

  if (token === null || token === '') {
    return (
      <main className="pre-session" dir="rtl">
        <h1>{t('signup.verify.title')}</h1>
        <StateBlock
          titleHe={t('signup.verify.noTokenTitle')}
          bodyHe={t('signup.verify.noTokenBody')}
          nextStep={
            <p>
              <a href={routes.publicSignup}>{t('signup.title')}</a>
            </p>
          }
        />
      </main>
    );
  }

  return (
    <main className="pre-session" dir="rtl">
      <h1>{t('signup.verify.title')}</h1>

      {stage.kind === 'verifying' ? (
        <p aria-live="polite">{t('signup.verify.working')}</p>
      ) : stage.kind === 'failed' ? (
        <ActionFailureNotice failure={stage.failure} />
      ) : stage.kind === 'created' ? (
        <StateBlock
          titleHe={t('signup.createdTitle')}
          bodyHe={t('signup.createdBody')}
          nextStep={
            <p>
              <a href={routes.signIn}>{t('signup.goSignIn')}</a>
            </p>
          }
        />
      ) : (
        <>
          <StateBlock titleHe={t('signup.verify.verified')} bodyHe={stage.legalName} />

          <form className="form" onSubmit={(event) => create(event, stage.signupUid)}>
            <fieldset className="field">
              <legend className="field__label">{t('signup.plansTitle')}</legend>
              {offers.result.state === 'loaded' ? (
                offers.result.data.plans.length === 0 ? (
                  <p className="form__note">{t('signup.noPlans')}</p>
                ) : (
                  offers.result.data.plans.map((offer) => (
                    <label key={offer.planCode} className="choice">
                      <input
                        type="radio"
                        name="planCode"
                        value={offer.planCode}
                        checked={planCode === offer.planCode}
                        onChange={() => {
                          setPlanCode(offer.planCode);
                          /* The offer is per plan — see the state's own comment. */
                          setChosenSkus([]);
                        }}
                      />
                      <span>
                        <PlanLine offer={offer} />
                      </span>
                    </label>
                  ))
                )
              ) : offers.result.state === 'failed' ? (
                <ActionFailureNotice failure={offers.result.failure} />
              ) : (
                <p aria-live="polite">{t('signup.plansTitle')}</p>
              )}
            </fieldset>

            {/*
              ── THE ARTICLES BESIDE THE PLAN. **ITEM 1.** ──────────────────────────────────
              Rendered only once a plan is chosen, because the offer is the PLAN's. An estate
              whose articles are all `planned` offers none — and that is SAID, because an absent
              section reads as "there is nothing to add" and a sentence reads as the truth.
            */}
            {planCode === '' ? null : (
              <fieldset className="field" data-area="signup-addons">
                <legend className="field__label">{t('signup.addOns')}</legend>
                {addOnsForPlan.length === 0 ? (
                  <p className="form__note">{t('signup.noAddOns')}</p>
                ) : (
                  addOnsForPlan.map((addOn) => (
                    <label key={addOn.skuCode} className="choice">
                      <input
                        type="checkbox"
                        name="skuCodes"
                        value={addOn.skuCode}
                        checked={chosenSkus.includes(addOn.skuCode)}
                        onChange={(event) =>
                          setChosenSkus((current) =>
                            event.target.checked
                              ? [...current, addOn.skuCode].sort()
                              : current.filter((code) => code !== addOn.skuCode),
                          )
                        }
                      />
                      <span>
                        <strong>{addOn.displayNameHe}</strong>
                        {addOn.moduleNamesHe.length === 0
                          ? null
                          : ` · ${addOn.moduleNamesHe.join(' · ')}`}
                        {/* THE SERVER'S OWN STRING. This screen formats no money — see PlanLine. */}
                        {addOn.priceDisplay === null ? null : (
                          <>
                            {' · '}
                            <span dir="ltr">{addOn.priceDisplay}</span>
                          </>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </fieldset>
            )}

            <fieldset className="field">
              <legend className="field__label">{t('signup.kindLabel')}</legend>
              {KINDS.map((kind) => (
                <label key={kind.id} className="choice">
                  <input
                    type="radio"
                    name="tenantKind"
                    value={kind.id}
                    checked={tenantKind === kind.id}
                    onChange={() => setTenantKind(kind.id)}
                  />
                  <span>{t(kind.label)}</span>
                </label>
              ))}
            </fieldset>

            {/*
              ── THE FIGURE AND THE AGREEMENT TO IT, IN ONE BLOCK. **ITEM 1, 2026-11-03.** ────
              The checkbox says the customer agrees to the price, so the price has to be the one
              in front of them when they tick it. `.form` is a THREE-COLUMN GRID and every child
              takes a cell in source order: adding the articles fieldset above pushed the sum and
              the agreement into different columns on different rows, which the dom suite could
              not see and the browser showed at once. `.notice` spans the whole row — the one
              class in this stylesheet that says *"this belongs to the form and not to a column"*
              — so the two now read as one statement.

              A FAILED QUOTE SHOWS THE FAILURE AND NEVER THE LAST GOOD FIGURE: a stale total
              beside a live checkbox is an agreement nobody made.
            */}
            <section className="notice" data-area="signup-agreement">
              {planCode === '' ? null : (
                <CombinationQuote planCode={planCode} skuCodes={chosenSkus} />
              )}
              <label className="choice">
                <input
                  type="checkbox"
                  /*
                   * NAMED. A test selecting `input[type="checkbox"]` was selecting THE FIRST ONE,
                   * which was this control until item 1 put an articles fieldset above it -- and
                   * then the same selector ticked an article and left the agreement unticked.
                   */
                  name="consent"
                  checked={consent}
                  onChange={(event) => setConsent(event.target.checked)}
                />
                <span>{t('signup.consent')}</span>
              </label>
            </section>

            {/*
              ── STEP FOUR, AFTER THE AGREEMENT AND BEFORE THE OFFICE ──────────────────────────
              In the Owner's order, and after the consent for a reason: the figures are what the
              payment method is being checked FOR, and asking for a card before somebody has agreed
              to a price is asking them to trust a number they have not seen.
            */}
            <section className="tile">
              <h2 className="breakdown__title">{t('signup.payment.title')}</h2>
              <p className="form__note">{t('signup.payment.body')}</p>
              <p className="form__note">{t('signup.payment.pageBody')}</p>

              {payment.kind === 'checked' ? (
                <>
                  <p className="form__note">{t('signup.payment.checked')}</p>
                  {/*
                    WHO ANSWERED. The provider came back in the answer and was discarded, so this
                    screen said *"the payment method has been checked"* whoever had checked it --
                    and a harness answering a stub produced the same sentence a clearing company
                    produces. It is rendered for EVERY provider rather than for the ones we think
                    are not real: a list of *"simulated"* names here would be a second place to
                    keep, and the one that got out of date would be the one that lied.
                  */}
                  <p className="form__note">
                    {t('signup.payment.checkedWith')}
                    <strong>{payment.provider}</strong>
                  </p>
                </>
              ) : (
                <>
                  {payment.kind === 'refused' ? (
                    <ActionFailureNotice failure={payment.failure} />
                  ) : null}
                  {payment.kind === 'leaving' ? (
                    <p className="form__note" aria-live="polite">
                      {t('card.leaving')}
                    </p>
                  ) : null}
                  <div className="actions-row">
                    {/*
                      ── TWO AFFORDANCES, AND ONLY ONE IS OFFERED AT A TIME ──────────────────────
                      The redirect is the real journey and the direct check is what remains when
                      this estate has no provider page. Showing both at once would offer a person
                      a choice between a mechanism and its absence.

                      `btn--nav` AND NOT `btn--primary` on both: there is ONE primary per form and
                      it is the create button below, which is the write.
                      `components/actionAffordance.test.ts` rule 3 refuses a second.
                    */}
                    {payment.kind === 'noProviderPage' ? (
                      <button
                        type="button"
                        className="btn btn--nav"
                        disabled={payment.kind !== 'noProviderPage' || !consent || planCode === ''}
                        onClick={() => checkPaymentMethod(stage.signupUid)}
                      >
                        {t('signup.payment.check')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--nav"
                        disabled={
                          payment.kind === 'asking' ||
                          payment.kind === 'leaving' ||
                          payment.kind === 'checking' ||
                          !consent ||
                          planCode === ''
                        }
                        onClick={() => goToPaymentPage(stage.signupUid)}
                      >
                        {t('signup.payment.goToProvider')}
                      </button>
                    )}
                  </div>
                </>
              )}
            </section>

            <div className="actions-row">
              {/*
                DISABLED UNTIL ALL THREE ARE PRESENT, and the server refuses either way. The
                control is disabled here because a person who has not chosen a plan has nothing to
                agree TO — which is a different thing from the server's refusal, and the server's is
                the one that decides.

                THE THIRD IS THE PAYMENT CHECK, ו-16. Three independent places refuse a signup with
                no payment method — `complete`, the store's predicate, and
                `CK_signup_request_completed_has_payment_method` — and NONE of them is this. A
                disabled button is manners; the floor is in the database.
              */}
              <button
                type="submit"
                className="btn btn--primary"
                disabled={sending || !consent || planCode === '' || payment.kind !== 'checked'}
              >
                {t('signup.create')}
              </button>
            </div>
          </form>
        </>
      )}
    </main>
  );
}

/**
 * §9.6'S FIGURES ON THE SECOND SCREEN, AND THE SAME DEFECT THE FIRST ONE ALREADY FIXED.
 *
 * ══ FOUND BY RENDERING IT, 2026-11-01. ══════════════════════════════════════════
 *
 * This line read `${offer.price} ${offer.currencyCode}` and produced **`390.0000 ILS`** — the
 * exact shape the Owner named as wrong on 2026-10-30: *"הצג מחיר מקומי קריא, למשל `100.00 ₪` …
 * לא `390.0000 ILS`"*. `PublicSignupScreen` was corrected that day and `PlanOffer` carries the
 * note; THIS screen shows the same offers and was missed, because nothing had ever rendered it.
 *
 * THE SUITES COULD NOT SEE IT AND WERE NOT WRONG TO MISS IT. `publicSignup.dom.test.tsx` drives
 * this screen's BEHAVIOUR — what it sends, what it refuses to send, which button opens when — and
 * a four-decimal price is a correct string in every one of those assertions. It is visible only
 * on a screen, which is what the harness is for and is why this screen is now in it.
 *
 * THE TOTAL IS SHOWN BESIDE THE PRICE, because that is the figure the person is about to agree to
 * and the consent checkbox two lines below says so. When no VAT rate is recorded all three
 * displays are null together: the plan is still sellable and the ex-VAT price is still true, and
 * what cannot be claimed is a total.
 *
 * THE RAW FIGURE IS THE FALLBACK AND NOT THE DEFAULT — `formatAmount` answers null only for an
 * amount it cannot parse, and showing something true and ugly beats showing nothing.
 */
function PlanLine({ offer }: { readonly offer: PublicPlanOfferView }): JSX.Element {
  const { t } = useLocale();
  return (
    <>
      {offer.displayNameHe}
      {' · '}
      <span dir="ltr">{offer.priceDisplay ?? `${offer.price} ${offer.currencyCode}`}</span>
      {/*
        `== null` AND NOT `=== null`, WHICH IS THE OPPOSITE OF THIS REPOSITORY'S USUAL RULE AND IS
        RIGHT HERE. Found by rendering: the strict form printed *"סך הכול לתשלום: undefined"* on a
        payload whose `totalDisplay` was ABSENT rather than null.

        The contract types it `string | null`, and a contract is a claim about what the server
        SHOULD send; this value arrives over a network from a build that may be older than this
        one. A nullish check covers both shapes, and the alternative — trusting the type — is the
        recorded lesson that `interpretResponse`'s own narrowing follows everywhere else.
      */}
      {offer.totalDisplay == null ? null : (
        <>
          {' · '}
          <span dir="ltr">{`${t('signup.total')}: ${offer.totalDisplay}`}</span>
        </>
      )}
      {' · '}
      {`${t('signup.trial')} ${String(offer.trialDays)} ${t('signup.trialDays')}`}
      {' · '}
      <span dir="ltr">{`${t('signup.firstCharge')}: ${offer.firstChargeOn}`}</span>
    </>
  );
}

/**
 * WHAT THE CHOSEN COMBINATION COSTS — §9.6's three figures, from the server. **ITEM 1.**
 *
 * A COMPONENT AND NOT A HOOK IN THE SCREEN, because `useServerData` reads on mount and has no
 * "not yet" state: asking for a quote before a plan is chosen would send a request the server
 * refuses with `plan_not_public`, on every arrival, for a question nobody asked. Mounting it with
 * the plan is the same condition expressed where React can act on it.
 *
 * IT FORMATS NOTHING. `totalDisplay` is `formatAmount`'s own string — *"הצג מחיר מקומי קריא,
 * למשל `100.00 ₪`… לא `390.0000 ILS`"* — and the screen prints it.
 *
 * A FAILED QUOTE SHOWS THE FAILURE AND NOT THE LAST GOOD FIGURE. A stale total beside a live
 * consent checkbox is an agreement to a number nobody computed.
 */
function CombinationQuote({
  planCode,
  skuCodes,
}: {
  readonly planCode: string;
  readonly skuCodes: readonly string[];
}): JSX.Element {
  const { t } = useLocale();
  /*
   * KEYED ON THE JOINED CODES. The screen holds the selection SORTED, so two selections of the
   * same articles produce the same key and the server is not asked again for a choice that has
   * not changed.
   */
  const key = skuCodes.join(',');
  const read = useCallback(
    () =>
      callOperation('public.signup.quote', {
        body: { planCode, skuCodes: key === '' ? [] : key.split(',') },
      }),
    [planCode, key],
  );
  const quote = useServerData(read);

  return (
    <div data-area="signup-quote">
      {quote.result.state === 'loaded' ? (
        <p className="form__note">
          <strong dir="ltr">
            {quote.result.data.totalDisplay ?? quote.result.data.priceDisplay ?? ''}
          </strong>
          {/*
            NO VAT RATE MEANS NO TOTAL, AND THE SENTENCE SAYS SO. 0035: a missing rate is a real
            state and must not be invented — showing the ex-VAT price labelled as a total would be
            a tax exemption nobody granted.
          */}
          {quote.result.data.totalDisplay === null
            ? ` ${t('signup.noVatRate')}`
            : ` ${t('signup.total')}`}
        </p>
      ) : quote.result.state === 'failed' ? (
        <ActionFailureNotice failure={quote.result.failure} />
      ) : (
        <p aria-live="polite" className="form__note">
          {t('signup.quoting')}
        </p>
      )}
    </div>
  );
}
